'use client';

import { useClerk, useOrganizationList } from '@clerk/clerk-react';
import { Building2 } from 'lucide-react';
import { useState } from 'react';

import { OrganizationSwitcher } from '@/components/organization/OrganizationSwitcher';
import { Button, Card } from '@/components/ui';
import { switchActiveOrganization } from '@/lib/organization/switchActiveOrganization';
import { theme } from '@/lib/ui/theme';

/**
 * SelectOrganization is the terminal state for a signed-in user who belongs to organizations but
 * whose session has none active — the root's auto-select never landed, usually because Clerk
 * rejected it.
 *
 * It carries its own action rather than leaning on OrganizationSwitcher alone: the switcher
 * self-gates to render nothing at one membership ("a single-organization user has nothing to
 * switch to"), and a single-membership user whose setActive failed is the most likely visitor
 * here. A card whose only control renders away would be the same dead end this replaces.
 */
export function SelectOrganization() {
  const { setActive } = useClerk();
  const { isLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: false },
  });
  const [isSwitching, setIsSwitching] = useState(false);

  const memberships = userMemberships?.data ?? [];
  const only = memberships.length === 1 ? memberships[0] : undefined;

  const handleContinue = async () => {
    if (!only) return;
    setIsSwitching(true);
    // switchActiveOrganization navigates on success, so this only unsets on failure — where the
    // helper has already surfaced a toast and the button must become pressable again.
    const ok = await switchActiveOrganization(setActive, only.organization.id);
    if (!ok) setIsSwitching(false);
  };

  return (
    <Card className="border-primary/20 mx-auto max-w-md">
      <div className="flex flex-col items-center gap-4 py-2 text-center">
        <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
          <Building2 className="h-6 w-6" />
        </div>
        <div className="space-y-1.5">
          <h2
            className="text-xl font-semibold"
            style={{ fontFamily: theme.fonts.body }}
          >
            Choose an organization
          </h2>
          <p
            className="text-muted-foreground text-sm"
            style={{ fontFamily: theme.fonts.body }}
          >
            You&rsquo;re signed in, but no organization is active for this
            session. Pick one to load its dashboard.
          </p>
        </div>
        {/* This component reads its own membership list, which starts empty. Falling through to
            the switcher during that window would render nothing at all, since the switcher
            self-gates at one membership — the same control-free card this component exists to
            avoid. Show a disabled control until the list is known. */}
        {!isLoaded ? (
          <Button disabled data-testid="continue-to-organization-loading">
            Loading organizations…
          </Button>
        ) : only ? (
          <Button
            onClick={handleContinue}
            disabled={isSwitching}
            data-testid="continue-to-organization"
          >
            {isSwitching ? 'Opening…' : `Continue to ${only.organization.name}`}
          </Button>
        ) : (
          <OrganizationSwitcher />
        )}
      </div>
    </Card>
  );
}
